import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CurrencyAmount } from '@core/models/currency-amount.model';
import { convert, CurrencyExchange } from '@core/models/currency-exchange';
import { InsuranceTarget } from '@core/models/insurance-target.model';
import { ProductCoverage } from '@core/models/product-coverage.model';
import { Product } from '@core/models/product.model';
import { LiteService } from '@core/services/lite.service';
import { LanguageService } from '@i18n/services/language.service';
import { getFieldError, isFieldInvalid, isFieldValid } from '@shared/utils/forms/validations.util';

@Component({
  selector: 'mfp-product-feature-coverage',
  templateUrl: './product-feature-coverage.component.html',
  styleUrls: ['./product-feature-coverage.component.scss']
})
export class ProductFeatureCoverageComponent {
  @Input() coverage!: ProductCoverage;
  @Input() insuranceTarget!: InsuranceTarget;
  @Input() product!: Product;
  @Input() currencyExchange!: CurrencyExchange;
  @Input() disabled = true;
  @Input() placeholder = '';
  @Output() coverageChange = new EventEmitter<ProductCoverage>();

  isFieldValid = isFieldValid;
  isFieldInvalid = isFieldInvalid;
  getFieldError = getFieldError;

  constructor(private language: LanguageService, private liteService: LiteService) {}

  getValue() {
    const amount = this.getAmount();
    switch (this.coverage.valueType) {
      case 'number':
        return amount;
      case 'percentage':
        let percentage = 0;
        switch (this.product.type) {
          case 'normal':
            const currentAssuredAmount = convert(
              this.product.currentAssuredAmount,
              this.product.selectedCurrency,
              this.currencyExchange
            );
            percentage = (amount / currentAssuredAmount.amount) * 100;
            break;
          case 'investment':
            const amountAtEndOfTemporality = convert(
              this.product.amountAtEndOfTemporality,
              this.product.selectedCurrency,
              this.currencyExchange
            );
            percentage = (amount / amountAtEndOfTemporality.amount) * 100;
            break;
        }
        return `${Math.round(percentage * 100) / 100}%`;
    }
  }

  getAmount() {
    if (this.coverage.currentAmount) {
      return Math.round(
        convert(this.coverage.currentAmount, this.product.selectedCurrency, this.currencyExchange).amount
      );
    }
    return 0;
  }

  getMin() {
    if (this.coverage.amountLimits?.min.amount) {
      const currencyAmount: CurrencyAmount = {
        amount: this.coverage.amountLimits?.min.amount,
        currency: this.coverage.amountLimits?.min.currency
      };
      return Math.round(convert(currencyAmount, this.product.selectedCurrency, this.currencyExchange).amount);
    }
    return 0;
  }

  getMax() {
    if (this.coverage.amountLimits?.max.amount) {
      const currencyAmount: CurrencyAmount = {
        amount: this.coverage.amountLimits?.max.amount,
        currency: this.coverage.amountLimits?.max.currency
      };
      return Math.round(convert(currencyAmount, this.product.selectedCurrency, this.currencyExchange).amount);
    }
    return 0;
  }

  onChangeActive(active: boolean) {
    this.coverage.active = active;
    if (!this.coverage.currentAmount) {
      this.coverage.valid = false;
    }
    this.coverageChange.emit(this.coverage);
  }

  onChangeAmount(event: string) {
    if (isNaN(+event)) {
      event = event.replace('%', '');
      if (isNaN(+event)) {
        return;
      }
      switch (this.product.type) {
        case 'normal':
          const currentAssuredAmount = convert(
            this.product.currentAssuredAmount,
            this.product.selectedCurrency,
            this.currencyExchange
          );
          event = (+event / 100) * currentAssuredAmount.amount + '';
          break;
        case 'investment':
          const amountAtEndOfTemporality = convert(
            this.product.amountAtEndOfTemporality,
            this.product.selectedCurrency,
            this.currencyExchange
          );
          event = (+event / 100) * amountAtEndOfTemporality.amount + '';
          break;
      }
    }
    const amount = +event;
    if (amount) {
      this.coverage.currentAmount = {
        currency: this.product.selectedCurrency,
        amount
      };
    } else {
      delete this.coverage.currentAmount;
    }

    if (this.getError() !== '') {
      this.coverage.valid = false;
    } else {
      this.coverage.valid = true;
    }
    this.liteService.coverageValueInsuredAmount.emit(false);
    this.coverageChange.emit(this.coverage);
  }

  getError(): string {
    if (!this.coverage.currentAmount) {
      return this.language.instant('PRODUCT_SELECT.MANDATORY_FIELD');
    }
    const amount = this.getAmount();
    if (this.coverage.amountLimits?.min.amount || this.coverage.amountLimits?.min.percentage) {
      const min = this.getMin();
      if (amount < min) {
        let percentage;
        if (this.coverage.percentageLimits?.min.percentage) {
          percentage = Math.round(this.coverage.percentageLimits?.min.percentage * 100) / 100;
        }
        return this.language.instant('PRODUCT_SELECT.COVERAGE.ERROR.MIN', {
          min: this.coverage.valueType === 'number' ? min : `${percentage}%`
        });
      }
    }
    if (this.coverage.amountLimits?.max.amount || this.coverage.amountLimits?.max.percentage) {
      const max = this.getMax();
      if (amount > max) {
        let percentage;
        if (this.coverage.percentageLimits?.max.percentage) {
          percentage = Math.round(this.coverage.percentageLimits?.max.percentage * 100) / 100;
        }
        return this.language.instant('PRODUCT_SELECT.COVERAGE.ERROR.MAX', {
          max: this.coverage.valueType === 'number' ? max : `${percentage}%`
        });
      }
    }
    return '';
  }
}
